/**
 * SharedProjectPage — Public page for shared project.
 * Route: /s/:shareToken
 * Unauthenticated clients browse view cards and generate videos.
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Typography, Spin, Empty, Result, Dropdown, Button } from "antd";
import { DownOutlined } from "@ant-design/icons";
import {
  collectionGroup,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import { subscribeToShareByToken, getShareTimespans } from "@/services/shareService";
import { convertPhotoUrls } from "@/utils/storage";
import type { ProjectShare, View, Photo } from "@/types";
import SharedViewCard from "@/components/SharedViewCard";

const { Text } = Typography;

export default function SharedProjectPage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [share, setShare] = useState<ProjectShare | null>(null);
  const [allViews, setAllViews] = useState<View[]>([]);
  const [latestPhotos, setLatestPhotos] = useState<Record<string, Photo | null>>({});
  const [activeViewPhotos, setActiveViewPhotos] = useState<Photo[]>([]);
  const [activeViewPhotosLoading, setActiveViewPhotosLoading] = useState(false);
  const [viewSelectorOpen, setViewSelectorOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"not_found" | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const photoUnsubsRef = useRef<Record<string, () => void>>({});

  // Subscribe to share document (live — reflects photographer edits instantly)
  useEffect(() => {
    if (!shareToken) return;

    const unsub = subscribeToShareByToken(shareToken, (s) => {
      if (!s) {
        setShare(null);
        setError("not_found");
        setLoading(false);
        return;
      }
      setError(null);
      setShare(s);
      setLoading(false);
    });

    return () => unsub();
  }, [shareToken]);

  // Subscribe to views once share is loaded (depends only on projectId —
  // excludedViewIds is applied in the memo below so it updates live).
  useEffect(() => {
    if (!share) return;

    const viewsQuery = query(
      collectionGroup(db, "views"),
      where("projectId", "==", share.projectId)
    );

    const unsub = onSnapshot(
      viewsQuery,
      (snapshot) => {
        const docs = snapshot.docs.map((doc) => ({
          ...doc.data(),
          viewId: doc.id,
        })) as View[];
        setAllViews(docs);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to load views:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [share?.projectId]);

  // Apply excludedViewIds and viewOrder live —
  // recomputes when photographer toggles inclusions or reorders views.
  const views = useMemo(() => {
    if (!share) return [];
    const visible = allViews.filter(
      (v) => !share.excludedViewIds.includes(v.viewId)
    );
    const order = share.viewOrder;
    if (!order || order.length === 0) return visible;
    const indexOf = new Map(order.map((id, i) => [id, i]));
    return [...visible].sort((a, b) => {
      const ai = indexOf.has(a.viewId) ? (indexOf.get(a.viewId) as number) : Number.MAX_SAFE_INTEGER;
      const bi = indexOf.has(b.viewId) ? (indexOf.get(b.viewId) as number) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
  }, [allViews, share]);

  // Keep slider index in range when the visible view list shrinks
  useEffect(() => {
    if (views.length === 0) {
      if (currentIndex !== 0) setCurrentIndex(0);
      return;
    }
    if (currentIndex >= views.length) setCurrentIndex(views.length - 1);
  }, [views.length, currentIndex]);

  // Subscribe to latest photo per view (include userId to reuse existing composite index)
  useEffect(() => {
    // Clean up previous subscriptions
    Object.values(photoUnsubsRef.current).forEach((unsub) => unsub());
    photoUnsubsRef.current = {};

    if (!share) return;

    views.forEach((view) => {
      const photosQuery = query(
        collection(db, "photos"),
        where("userId", "==", share.userId),
        where("viewId", "==", view.viewId),
        orderBy("capturedAt", "desc"),
        limit(1)
      );

      const unsub = onSnapshot(photosQuery, (snapshot) => {
        if (snapshot.empty) {
          setLatestPhotos((prev) => ({ ...prev, [view.viewId]: null }));
          return;
        }
        const data = {
          ...snapshot.docs[0].data(),
          photoId: snapshot.docs[0].id,
        } as any;
        setLatestPhotos((prev) => ({
          ...prev,
          [view.viewId]: convertPhotoUrls(data),
        }));
      });

      photoUnsubsRef.current[view.viewId] = unsub;
    });

    return () => {
      Object.values(photoUnsubsRef.current).forEach((unsub) => unsub());
      photoUnsubsRef.current = {};
    };
  }, [share, views]);

  // Subscribe to ALL photos of the active view (used by the inline mini-player).
  // Only one view at a time is mounted, keeping the read volume bounded.
  useEffect(() => {
    if (!share) {
      setActiveViewPhotos([]);
      return;
    }
    const activeView = views[currentIndex];
    if (!activeView) {
      setActiveViewPhotos([]);
      return;
    }

    setActiveViewPhotosLoading(true);
    const photosQuery = query(
      collection(db, "photos"),
      where("userId", "==", share.userId),
      where("viewId", "==", activeView.viewId),
      orderBy("capturedAt", "desc")
    );

    const unsub = onSnapshot(
      photosQuery,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => {
          const data = { ...doc.data(), photoId: doc.id } as any;
          return convertPhotoUrls(data);
        });
        setActiveViewPhotos(list);
        setActiveViewPhotosLoading(false);
      },
      () => setActiveViewPhotosLoading(false)
    );

    return () => unsub();
  }, [share, views, currentIndex]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Result
          status="404"
          title="Share not found"
          subTitle="This shared link does not exist or has been removed."
        />
      </div>
    );
  }

  if (!share) return null;

  if (!share.enabled) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Result
          status="403"
          title="Sharing disabled"
          subTitle="The photographer has disabled sharing for this project."
        />
      </div>
    );
  }

  const currentView = views[currentIndex] ?? null;
  const hasMultiple = views.length > 1;

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      {/* Header — logo only */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #f0f0f0",
          padding: "12px 24px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {share.logoUrl ? (
          <img
            src={share.logoUrl}
            alt={share.photographerName}
            style={{ height: 40, maxWidth: 160, objectFit: "contain" }}
          />
        ) : (
          <Text strong style={{ fontSize: 16 }}>
            {share.photographerName}
          </Text>
        )}
      </div>

      {/* Project name (left) + view selector dropdown (right), above the player */}
      <div
        style={{
          padding: "16px 24px 0",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          gap: 16,
          maxWidth: 1440,
          margin: "0 auto",
          width: "100%",
        }}
      >
        <span
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: "#262626",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
          title={share.projectName}
        >
          {share.projectName}
        </span>

        {hasMultiple && (
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            open={viewSelectorOpen}
            onOpenChange={setViewSelectorOpen}
            dropdownRender={() => (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 8,
                  boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                  padding: 6,
                  minWidth: 280,
                  maxHeight: 420,
                  overflowY: "auto",
                }}
              >
                {views.map((v, i) => {
                  const thumb = latestPhotos[v.viewId];
                  const active = i === currentIndex;
                  return (
                    <div
                      key={v.viewId}
                      onClick={() => {
                        setCurrentIndex(i);
                        setViewSelectorOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: 8,
                        borderRadius: 6,
                        cursor: "pointer",
                        background: active ? "#e6f4ff" : "transparent",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        if (!active) e.currentTarget.style.background = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        if (!active) e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <div
                        style={{
                          width: 64,
                          height: 40,
                          borderRadius: 4,
                          overflow: "hidden",
                          flex: "0 0 auto",
                          background:
                            "radial-gradient(ellipse at center, #e0e0e0 0%, #9e9e9e 100%)",
                          border: active ? "2px solid #1890ff" : "2px solid transparent",
                        }}
                      >
                        {thumb && (
                          <img
                            src={thumb.urls.thumb200}
                            alt={v.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        )}
                      </div>
                      <Text
                        style={{
                          fontSize: 14,
                          fontWeight: active ? 600 : 400,
                          color: active ? "#1890ff" : "#262626",
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={v.name}
                      >
                        {v.name}
                      </Text>
                    </div>
                  );
                })}
              </div>
            )}
          >
            <Button>
              <span
                style={{
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              >
                {currentView?.name ?? "Select view"}
              </span>
              <DownOutlined style={{ marginLeft: 8 }} />
            </Button>
          </Dropdown>
        )}
      </div>

      {/* Player block */}
      <div
        style={{
          padding: "16px 24px",
          boxSizing: "border-box",
          maxWidth: 1440,
          margin: "0 auto",
          width: "100%",
        }}
      >
        {views.length === 0 || !currentView ? (
          <Empty description="No views available" />
        ) : (
          <SharedViewCard
            key={currentView.viewId}
            view={currentView}
            projectName={share.projectName}
            latestPhoto={latestPhotos[currentView.viewId] ?? null}
            viewPhotos={activeViewPhotos}
            viewPhotosLoading={activeViewPhotosLoading}
            shareId={share.shareId}
            shareToken={share.shareToken}
            allowedTimespans={getShareTimespans(share)}
            galleryEnabled={share.galleryEnabled ?? true}
            videoEnabled={share.videoEnabled ?? true}
            onOpenView={(viewId, mode) =>
              navigate(`/s/${share.shareToken}/${viewId}?mode=${mode}`)
            }
          />
        )}
      </div>
    </div>
  );
}
